import { Button } from "@/components/ui/button";
import { useStepper } from "@/components/ui/stepper";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useLocalStorage } from "@/hooks/useStorage";
import { useTranslation } from "@/hooks/useTranslation";
import {
  SetupTeamCreationInfosDTO,
  SetupTeamCreationSurveyDTO,
  TeamDTO,
} from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import DinoGame from "react-chrome-dino-ts";
import { FiChevronLeft } from "react-icons/fi";

let isSetupCreating = false;

export const SetupTeamCreationCreatePage = () => {
  const { setTeamSelected } = useTeams();
  const { nextStep, prevStep, setStep, activeStep } = useStepper();
  const api = useApi();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const [setupTeamCreationSurveyDatas] =
    useLocalStorage<SetupTeamCreationSurveyDTO>("setup-team-creation-survey");
  const [setupTeamCreationInfosDatas] =
    useLocalStorage<SetupTeamCreationInfosDTO>("setup-team-creation-infos");
  const [referralCode] = useLocalStorage<string>("referralCode");

  const [error, setError] = useState(false);

  useEffect(() => {
    if (isSetupCreating) return;
    isSetupCreating = true;
    createTeam();
  }, []);

  const createTeam = async () => {
    try {
      const { data } = await api.post<TeamDTO>(`/my/team/setup`, {
        survey: {
          comeFrom: setupTeamCreationSurveyDatas?.comeFrom || [],
          comeFromOther: setupTeamCreationSurveyDatas?.comeFromOther || "",
          potentialUsersCount:
            setupTeamCreationSurveyDatas?.potentialUsersCount || "",
          whatWaitingFor: setupTeamCreationSurveyDatas?.whatWaitingFor || [],
          whatWaitingForOther:
            setupTeamCreationSurveyDatas?.whatWaitingForOther || "",
          comment: setupTeamCreationSurveyDatas?.comment || "",
        },
        infos: {
          jobTitle: setupTeamCreationInfosDatas?.jobTitle || null,
          siret: setupTeamCreationInfosDatas?.siret || null,
          website: setupTeamCreationInfosDatas?.website || null,
          linkedin: setupTeamCreationInfosDatas?.linkedin || null,
          instagram: setupTeamCreationInfosDatas?.instagram || null,
          referralCode: referralCode || null,
        },
      });
      setTeamSelected(data);

      if (
        setupTeamCreationInfosDatas?.siret ||
        setupTeamCreationInfosDatas?.website
      ) {
        nextStep();
      } else {
        setStep(activeStep + 2);
      }
    } catch (error) {
      isSetupCreating = false;
      setError(error as any);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col space-y-5">
        <p className="text-center">
          {t("setup.teamCreation.create.error.title")}
        </p>
        <p className="text-center">
          {t("setup.teamCreation.create.error.description")}{" "}
          <a href="mailto:support@shaarp.ai">support@shaarp.ai</a>
        </p>
        <div className="text-center">
          <Button onClick={() => prevStep()} variant="outline">
            <FiChevronLeft className="mr-2 w-4 h-4" />
            {t("setup.teamCreation.create.buttons.previous")}
          </Button>
        </div>
      </div>
    );
  }

  if (!setupTeamCreationInfosDatas?.siret) {
    return (
      <div className="w-full text-center">
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamCreation.create.loading.title")}
        </h1>
      </div>
    );
  }

  return (
    <div className="space-y-10 w-full text-center">
      <div>
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamCreation.create.loading.configTitle")}
        </h1>
        <p className="text-sm">
          {t("setup.teamCreation.create.loading.description")}
        </p>
        {!isMobile && (
          <p className="text-sm">
            {t("setup.teamCreation.create.loading.gameInvitation")}
          </p>
        )}
      </div>
      <div>
        {activeStep === 3 &&
          (isMobile ? (
            <div className="flex flex-col justify-center items-center w-full h-full">
              <p>
                <Loader2Icon className="w-10 h-10 animate-spin" />
              </p>
            </div>
          ) : (
            <DinoGame
              instructions={t(
                "setup.teamCreation.create.loading.gameInstructions"
              )}
            />
          ))}
      </div>
    </div>
  );
};
